import { z } from "zod";

import { apiSlice } from "api/api.slice";
import type { Knowledge, SimilarDimensionArg } from "api/api.types";
import { QUERY_LIMIT } from "api/constants";
import { knowledgeSchema } from "api/schemas/knowledgeApiSchema";
import type { ContentTypes } from "api/types/knowledge";
import { toSearchString } from "api/utils";
import { updateTopicPinnedKnowledgeFulfilled } from "domains/pinAddReject/actions";
import type { KnowledgeStatus } from "entities/knowledge/types";

type FetchKnowledgeStatusByIdPayload = {
  status: KnowledgeStatus;
};

export const knowledgeApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getKnowledgeById: build.query<Knowledge, { id: string }>({
      query: ({ id }) => ({
        url: `/knowledge/${id}`,
        schema: knowledgeSchema,
      }),
    }),
    getKnowledgeStatusById: build.query<FetchKnowledgeStatusByIdPayload, { id: string }>({
      query: ({ id }) => ({
        method: "GET",
        url: `/knowledge/${id}/status`,
      }),
    }),
    getSimilarKnowledgeByDimension: build.query<Knowledge[], SimilarDimensionArg>({
      query: ({ body, search }) => ({
        body,
        method: "POST",
        url: `/knowledge/similar/dimension${toSearchString(search)}`,
        schema: z.array(knowledgeSchema),
      }),
    }),
    searchPublications: build.infiniteQuery<Knowledge[], { searchQuery: string; sourceNames: string[] }, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam, queryArg: { searchQuery, sourceNames } }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: "/publication",
          params: {
            search: searchQuery,
            limit: QUERY_LIMIT,
            offset,
            source_name: sourceNames,
          },
          schema: z.array(knowledgeSchema),
        };
      },
    }),
    searchVideos: build.infiniteQuery<Knowledge[], { searchQuery: string; sourceNames: string[] }, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam, queryArg: { searchQuery, sourceNames } }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: "/media",
          params: {
            content_type: "Videos",
            search: searchQuery,
            limit: QUERY_LIMIT,
            offset,
            source_name: sourceNames,
          },
          schema: z.array(knowledgeSchema),
        };
      },
    }),
    searchInteractiveData: build.infiniteQuery<Knowledge[], { searchQuery: string; sourceNames: string[] }, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam, queryArg: { searchQuery, sourceNames } }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: "/knowledge_data",
          params: {
            search: searchQuery,
            limit: QUERY_LIMIT,
            offset,
            source_name: sourceNames,
          },
          schema: z.array(knowledgeSchema),
        };
      },
    }),
    addKnowledge: build.mutation<
      { knowledge_id: string },
      { link: string; topicIds: string[]; contentType?: ContentTypes }
    >({
      query: ({ link, topicIds, contentType }) => ({
        method: "POST",
        url: "/knowledge",
        body: {
          link,
          topic_ids: topicIds,
          content_type: contentType,
        },
      }),
    }),
    getPinnedKnowledgeByTopic: build.infiniteQuery<Knowledge[], { topicId: string }, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam, queryArg: { topicId } }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: `/topic/${topicId}/knowledge`,
          params: {
            pinned: true,
            limit: QUERY_LIMIT,
            offset,
          },
          schema: z.array(knowledgeSchema),
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
        };
      },
      providesTags: (_result, _error, arg) => [{ type: "Topics", id: arg.topicId }, "Knowledge", "Pinned"],
    }),
    pinKnowledgeToTopic: build.mutation<unknown, { topicId: string; knowledgeId: string }>({
      query: ({ topicId, knowledgeId }) => ({
        method: "PATCH",
        url: `/topic/${topicId}/knowledge/pin`,
        body: {
          add: [knowledgeId],
          remove: [],
        },
      }),
      onQueryStarted: async ({ topicId, knowledgeId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(updateTopicPinnedKnowledgeFulfilled({ topicId, knowledgeId, isPinned: true }));
        } catch {
          dispatch(updateTopicPinnedKnowledgeFulfilled({ topicId, knowledgeId, isPinned: false }));
        }
      },
      invalidatesTags: (_result, _error, { topicId }) => [{ type: "Topics", id: topicId }, "Knowledge", "Pinned"],
    }),
    unpinKnowledgeFromTopic: build.mutation<unknown, { topicId: string; knowledgeId: string }>({
      query: ({ topicId, knowledgeId }) => ({
        method: "PATCH",
        url: `/topic/${topicId}/knowledge/pin`,
        body: {
          add: [],
          remove: [knowledgeId],
        },
      }),
      onQueryStarted: async ({ topicId, knowledgeId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          knowledgeApiSlice.util.updateQueryData("getPinnedKnowledgeByTopic", { topicId }, (draft) => {
            const index = draft.pages.findIndex((page) => page.some((knowledge) => knowledge.id === knowledgeId));

            draft.pages[index] = draft.pages[index].filter((knowledge) => knowledge.id !== knowledgeId);
          }),
        );

        try {
          await queryFulfilled;
          dispatch(updateTopicPinnedKnowledgeFulfilled({ topicId, knowledgeId, isPinned: false }));
        } catch {
          patchResult.undo();
          dispatch(updateTopicPinnedKnowledgeFulfilled({ topicId, knowledgeId, isPinned: true }));
        }
      },
      invalidatesTags: (_result, _error, { topicId }) => [{ type: "Topics", id: topicId }, "Knowledge", "Pinned"],
    }),
  }),
});

export const { useLazyGetSimilarKnowledgeByDimensionQuery } = knowledgeApiSlice;
